import { Fragment } from "react";
import { View } from "react-native";
import Svg, {
  Path,
  Circle,
  Line,
  Text as SvgText,
  Defs,
  LinearGradient as SvgLinearGradient,
  Stop,
} from "react-native-svg";
import { EMBER } from "../../onboarding/theme";

const CHART_W = 280;
const CHART_H = 100;
const PAD_L = 46;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 24;
const PLOT_W = CHART_W - PAD_L - PAD_R;
const PLOT_H = CHART_H - PAD_T - PAD_B;

/** Shared chronological result trend chart used by consolidated test routes. */
export function HistoryChart({
  history,
  unit: _unit,
  isAbnormal,
}: {
  history: { date: string; value: string }[];
  unit: string;
  isAbnormal?: boolean;
}) {
  if (history.length < 1) return null;

  const numericPoints = history
    .map((point) => ({
      date: point.date,
      num: parseFloat(point.value.replace(/,/g, "")),
    }))
    .filter((point) => !isNaN(point.num));

  if (numericPoints.length < 1) return null;

  const minVal = Math.min(...numericPoints.map((point) => point.num));
  const maxVal = Math.max(...numericPoints.map((point) => point.num));
  const observedRange = maxVal - minVal;
  const padded = observedRange
    ? observedRange * 0.15
    : Math.max(Math.abs(maxVal) * 0.15, 0.1);
  const chartMin = minVal - padded;
  const chartMax = maxVal + padded;
  const chartRange = chartMax - chartMin;
  const yForValue = (value: number) =>
    PAD_T + PLOT_H - ((value - chartMin) / chartRange) * PLOT_H;
  const formatAxisValue = (value: number) =>
    Number(value.toFixed(2)).toString();
  const axisTicks = observedRange
    ? [
        { value: maxVal, label: formatAxisValue(maxVal) },
        {
          value: minVal + observedRange / 2,
          label: formatAxisValue(minVal + observedRange / 2),
        },
        { value: minVal, label: formatAxisValue(minVal) },
      ]
    : [
        { value: chartMax, label: formatAxisValue(chartMax) },
        { value: maxVal, label: formatAxisValue(maxVal) },
        { value: chartMin, label: formatAxisValue(chartMin) },
      ];

  const points = numericPoints.map((point, index) => ({
    x:
      numericPoints.length === 1
        ? PAD_L + PLOT_W / 2
        : PAD_L + (index / (numericPoints.length - 1)) * PLOT_W,
    y: yForValue(point.num),
    date: point.date,
    value: point.num,
  }));

  let linePath = points.length > 1 ? `M${points[0]!.x},${points[0]!.y}` : "";
  for (let index = 0; index < points.length - 1; index++) {
    const current = points[index]!;
    const next = points[index + 1]!;
    const controlPoint = (current.x + next.x) / 2;
    linePath += ` C${controlPoint},${current.y} ${controlPoint},${next.y} ${next.x},${next.y}`;
  }

  const last = points[points.length - 1]!;
  const first = points[0]!;
  const areaPath = linePath
    ? linePath + ` L${last.x},${PAD_T + PLOT_H} L${first.x},${PAD_T + PLOT_H} Z`
    : null;
  const lineColor = isAbnormal ? EMBER.colors.coral : "#6B5548";

  return (
    <View
      style={{
        backgroundColor: "rgba(0,0,0,0.02)",
        borderRadius: 12,
        padding: 8,
        marginTop: 4,
      }}
    >
      <Svg width="100%" height={CHART_H} viewBox={`0 0 ${CHART_W} ${CHART_H}`}>
        <Defs>
          <SvgLinearGradient id="labAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0%" stopColor={lineColor} stopOpacity="0.15" />
            <Stop offset="100%" stopColor={lineColor} stopOpacity="0" />
          </SvgLinearGradient>
        </Defs>
        {axisTicks.map((tick, index) => {
          const y = yForValue(tick.value);
          return (
            <Fragment key={`axis-${index}`}>
              <Line
                x1={PAD_L}
                y1={y}
                x2={CHART_W - PAD_R}
                y2={y}
                stroke="rgba(0,0,0,0.04)"
                strokeWidth={1}
              />
              <SvgText
                x={PAD_L - 6}
                y={y + 3}
                textAnchor="end"
                fontSize={8}
                fontFamily="DMSans_400Regular"
                fill="rgba(0,0,0,0.35)"
              >
                {tick.label}
              </SvgText>
            </Fragment>
          );
        })}
        {areaPath ? <Path d={areaPath} fill="url(#labAreaGrad)" /> : null}
        {linePath ? (
          <Path
            d={linePath}
            fill="none"
            stroke={lineColor}
            strokeWidth={2}
            strokeLinecap="round"
          />
        ) : null}
        <Circle cx={last.x} cy={last.y} r={6} fill={`${lineColor}30`} />
        <Circle cx={last.x} cy={last.y} r={3} fill={lineColor} />
        <Circle cx={last.x} cy={last.y} r={1.5} fill="#fff" />
        {points.map((point, index) => (
          <SvgText
            key={index}
            x={point.x}
            y={CHART_H - 4}
            textAnchor="middle"
            fontSize={9}
            fontFamily="DMSans_400Regular"
            fill="rgba(0,0,0,0.3)"
            fontWeight={index === points.length - 1 ? "700" : "400"}
          >
            {point.date}
          </SvgText>
        ))}
      </Svg>
    </View>
  );
}
